import * as THREE from 'three';
import type { FoundationData } from '../../domain/foundation/calculateFoundation';
import { DEFAULT_SITE_CONDITIONS, getTerrainSlopeAtPoint } from '../../domain/foundation/siteConditions';
import { getDeckFootprintRects } from '../../domain/deck/deckFootprint';
import { addBox } from '../geometry';
import { setConcreteContactSpan } from '../concreteVariation';
import { matConcrete, matMetal, matTreatedWood } from '../materials';
import type { FoundationBuildOptions, SquareBaseBuildContext } from './context';

export function buildFoundation(
    ctx: SquareBaseBuildContext,
    foundation: FoundationData,
    group: THREE.Group,
    options: FoundationBuildOptions,
) {
    const beamY = -ctx.fH / 2;
    foundation.beams.forEach((beam) => {
        const support = addBox(beam.length, ctx.fH, ctx.fW, matTreatedWood, beam.x, beamY, beam.z, group);
        support.name = `detail/foundation/beam-${group.children.length}`;
    });

    const pierRadius = 0.08;
    foundation.plinths.forEach((plinth, index) => {
        if (plinth.visibleHeightM > 0.001) {
            const pierGeometry = new THREE.CylinderGeometry(pierRadius, pierRadius + 0.02, plinth.visibleHeightM, 16);
            // Lets matConcrete put its ground-contact band on the foot;
            // visibleHeightM varies per pier with the terrain slope.
            setConcreteContactSpan(pierGeometry, plinth.visibleHeightM);
            const pier = new THREE.Mesh(pierGeometry, matConcrete);
            pier.position.set(plinth.x, -ctx.fH - plinth.visibleHeightM / 2, plinth.z);
            pier.castShadow = true;
            pier.receiveShadow = true;
            pier.name = `detail/foundation/plinth-${index + 1}`;
            group.add(pier);
        }

        const shoe = addBox(ctx.fW + 0.01, 0.1, 0.05, matMetal, plinth.x, -ctx.fH / 2, plinth.z, group);
        shoe.name = `detail/foundation/shoe-${index + 1}`;
    });

    if (!options.includeEntrySteps) return;

    const entryPlacement = options.entryPlacement ?? {
        wall: 'front' as const,
        centerM: options.entryCenterXM ?? 0,
    };
    const siteConditions = options.siteConditions ?? DEFAULT_SITE_CONDITIONS;
    const baseGroundY = -ctx.fH - foundation.baselineVisibleHeightM;
    const getGroundY = (xM: number, zM: number) => baseGroundY + getTerrainSlopeAtPoint(siteConditions, xM, zM);
    const entryExtension = options.deckConfig?.attachWall === entryPlacement.wall
        ? Math.max(0, ...getDeckFootprintRects(options.deckConfig, { width: ctx.W, length: ctx.L }).map((rect) => rect.centerZ + rect.depth / 2))
        : 0;

    const stepWidth = Math.min(ctx.W * 0.46, 1.12);
    const stepDepth = 0.28;
    const stepThickness = 0.045;
    const entryFrontZ = ctx.L / 2 + entryExtension;
    // Long-wall entries step out sideways (±X); the front entry steps out in +Z.
    const isLongEntry = entryPlacement.wall === 'left' || entryPlacement.wall === 'right';
    const longSide = entryPlacement.wall === 'left' ? -1 : 1;
    const entrySideX = longSide * (ctx.W / 2 + entryExtension);
    const groundAtEntry = isLongEntry
        ? getGroundY(entrySideX + longSide * 0.52, entryPlacement.centerM)
        : getGroundY(entryPlacement.centerM, entryFrontZ + 0.52);
    const entryThresholdY = 0;
    const totalDrop = entryThresholdY - groundAtEntry;
    const stepRise = totalDrop / 3;
    const addEntryStep = (
        label: 'upper' | 'lower' | 'base',
        offsetM: number,
        riseMultiplier: number,
    ) => {
        const step = addBox(
            isLongEntry ? stepDepth : stepWidth,
            stepThickness,
            isLongEntry ? stepWidth : stepDepth,
            matTreatedWood,
            isLongEntry ? entrySideX + longSide * offsetM : entryPlacement.centerM,
            entryThresholdY - stepRise * riseMultiplier + stepThickness / 2,
            isLongEntry ? entryPlacement.centerM : entryFrontZ + offsetM,
            group,
        );
        step.name = `detail/entry/${options.namePrefix}-${label}-step`;
        return step;
    };

    const upperStep = addEntryStep('upper', 0.22, 0.5);
    addEntryStep('lower', 0.52, 1.5);
    addEntryStep('base', 0.82, 2.5);

    if (isLongEntry) {
        for (const [index, z] of [-0.34, 0.34].entries()) {
            const supportZ = entryPlacement.centerM + z;
            const supportTopY = upperStep.position.y - stepThickness / 2;
            const supportGroundY = getGroundY(entrySideX + longSide * 0.22, supportZ);
            const supportHeight = Math.max(0.08, supportTopY - supportGroundY);
            const support = addBox(
                0.08,
                supportHeight,
                0.08,
                matTreatedWood,
                entrySideX + longSide * 0.22,
                supportGroundY + supportHeight / 2,
                supportZ,
                group,
            );
            support.name = `detail/entry/${options.namePrefix}-upper-support-${index + 1}`;
        }
        return;
    }

    for (const [index, x] of [-0.34, 0.34].entries()) {
        const supportX = entryPlacement.centerM + x;
        const supportTopY = upperStep.position.y - stepThickness / 2;
        const supportGroundY = getGroundY(supportX, ctx.L / 2 + 0.22);
        const supportHeight = Math.max(0.08, supportTopY - supportGroundY);
        const support = addBox(
            0.08,
            supportHeight,
            0.08,
            matTreatedWood,
            supportX,
            supportGroundY + supportHeight / 2,
            entryFrontZ + 0.22,
            group,
        );
        support.name = `detail/entry/${options.namePrefix}-upper-support-${index + 1}`;
    }
}
